// Web-based Authentication using Firebase Auth
import { auth, googleProvider } from './firebase-config.js';
import {
    signInWithPopup,
    signOut as firebaseSignOut,
    onAuthStateChanged as firebaseOnAuthStateChanged
} from 'firebase/auth';

/**
 * Sign in with Google using popup
 * @returns {Promise<Object>} User object
 */
export async function signInWithGoogle() {
    try {
        const result = await signInWithPopup(auth, googleProvider);
        return {
            uid: result.user.uid,
            email: result.user.email,
            displayName: result.user.displayName,
            photoURL: result.user.photoURL
        };
    } catch (error) {
        console.error('Error signing in:', error);
        throw new Error(`Sign in failed: ${error.message}`);
    }
}

/**
 * Sign out current user
 * @returns {Promise<void>}
 */
export async function signOut() {
    try {
        await firebaseSignOut(auth);
    } catch (error) {
        console.error('Error signing out:', error);
        throw new Error(`Sign out failed: ${error.message}`);
    }
}

/**
 * Get current user
 * @returns {Object|null} Current user or null
 */
export function getCurrentUser() {
    const user = auth.currentUser;
    if (!user) return null;

    return {
        uid: user.uid,
        email: user.email,
        displayName: user.displayName,
        photoURL: user.photoURL
    };
}

/**
 * Listen for authentication state changes
 * @param {Function} callback - Called with user object or null
 * @returns {Function} Unsubscribe function
 */
export function onAuthStateChanged(callback) {
    return firebaseOnAuthStateChanged(auth, (user) => {
        if (user) {
            callback({
                uid: user.uid,
                email: user.email,
                displayName: user.displayName,
                photoURL: user.photoURL
            });
        } else {
            callback(null);
        }
    });
}

/**
 * Initialize authentication and return current user
 * @returns {Promise<Object|null>} Current user or null
 */
export async function initAuth() {
    return new Promise((resolve) => {
        const unsubscribe = firebaseOnAuthStateChanged(auth, (user) => {
            unsubscribe();
            if (user) {
                resolve({
                    uid: user.uid,
                    email: user.email,
                    displayName: user.displayName,
                    photoURL: user.photoURL
                });
            } else {
                resolve(null);
            }
        });
    });
}
